import {
  Flex,
  HorizontalSpacing,
  List,
  Typography,
  type ListRef,
} from '@banhala/ui-kit';
import { useContext, useMemo, useRef } from 'react';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import type { MarketCategory } from 'shared/src/api/marketService/getMarketCategories';
import { marketGoodsListFilterAtom } from '../../../states/GoodsListFilter.atom';
import { useAtom } from 'jotai';
import { useMarketCategories } from '../hooks/useMarketCategories';
import { ScrollContext } from '../../MarketHomeBanner/context/ScrollContext';
import { useLogApplyCategoryChipFilter } from '../hooks/useLogApplyCategoryChipFilter';
import { isNil } from 'shared/src/utils';

const ChipSeparator = () => <HorizontalSpacing size={4} />;

const AnimatedList = Animated.createAnimatedComponent(List<MarketCategory>);

interface Props {
  marketId: number;
}

export const SubCategories = ({ marketId }: Props) => {
  const logApplyCategoryChipFilter = useLogApplyCategoryChipFilter();

  const [value, setValue] = useAtom(marketGoodsListFilterAtom);
  const { data: categories } = useMarketCategories(marketId);
  const { subCategoriesScrollX } = useContext(ScrollContext);

  const categorySno = value[marketId].categorySno;

  const subCategories = useMemo(() => {
    if (isNil(categories)) return [];

    const foundCategory = categories?.categories.find(
      category => category.sno === categorySno,
    );

    if (isNil(categorySno) || isNil(foundCategory)) return [];

    const sortedSubCategoryByGoodsCount = [
      ...foundCategory.sub_categories,
    ].sort((a, b) => b.goods_count - a.goods_count);

    return [
      { ...foundCategory, name: '전체' },
      ...sortedSubCategoryByGoodsCount,
    ];
  }, [categories, categorySno]);

  const selectedSubCategorySno = value[marketId].subCategorySno;

  const categoriesListRef = useRef<ListRef<MarketCategory>>(null);

  const scrollHandler = useAnimatedScrollHandler(event => {
    if (!subCategoriesScrollX) return;

    subCategoriesScrollX.value = event.contentOffset.x;
  });

  const handleSelectSubCategory = (item: MarketCategory) => {
    logApplyCategoryChipFilter(item, 1);

    setValue(prev => ({
      ...prev,
      [marketId]: {
        ...prev[marketId],
        subCategorySno: item.sno,
      },
    }));
  };

  if (subCategories.length === 0) return <></>;

  return (
    <Flex backgroundColor="bg_secondary">
      <AnimatedList
        ref={categoriesListRef}
        onScroll={scrollHandler}
        onScrollToIndexFailed={() => {}}
        data={subCategories}
        renderItem={({ item }) => {
          const isSelected = selectedSubCategorySno === item.sno;
          return (
            <Flex
              padding={{ vertical: 12, horizontal: 8 }}
              onPress={() => handleSelectSubCategory(item)}
            >
              <Typography
                type={isSelected ? 'subtitle2' : 'body2'}
                color={isSelected ? 'content_primary' : 'content_secondary'}
              >
                {item.name}
              </Typography>
            </Flex>
          );
        }}
        contentContainerStyle={{
          paddingHorizontal: 12,
          zIndex: 3,
        }}
        showsHorizontalScrollIndicator={false}
        ItemSeparatorComponent={ChipSeparator}
        horizontal={true}
      />
    </Flex>
  );
};
